// Work Schedule page — PRD_WorkScheduleManagement.
//
// One row per ACTIVE employee showing their current employment.workSchedule
// (C4/C5); an edit modal changes one, a bulk modal assigns one schedule to
// many (C2/C3). Both write the same field the wizard writes, through
// workScheduleService's single dotted-path payload — never an employment
// object. Modelled on AdjustmentsPage.js (page frame, controls bar, table,
// footer per DESIGN-STANDARDS.md); the modals live in-file as the bulk-
// adjustments PRD kept its own (its C7).
//
// Page load (Architecture § Page): ONLY the employees query is fatal. The
// hoursPerDay chain (C16) and the payroll-index read (C7) are each caught
// separately — neither the summary bar's hour figures nor an advisory warning
// is worth blanking the roster.

import React, { useState, useEffect, useCallback, useMemo, useId } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../../contexts/ToastContext";
import Icon from "../icons/Icon";
import Modal from "../modals/Modal";
import { loadActiveLocks } from "../../services/dtrLockDerivation";
import { formatFilteredCount } from "../../utils/footerStats";
import { isEmployeeActiveOnDate } from "../../utils/employeeFilters";
import { matchesEmployeeSearch } from "../../utils/employeeSearch";
import { todayManilaYMD } from "../../utils/periodDate";
import { scheduleOtUt } from "../../utils/scheduleHours";
import {
  SCHEDULE_DEFAULTS,
  DEFAULT_WORKING_DAYS,
  buildSchedule,
  readSchedule,
  validateSchedule,
  draftRunCount,
  updateSchedule,
  assignScheduleBulk,
} from "../../services/workScheduleService";

// Chip / toggle order on THIS page (C4: Mon–Sun). Presentation only — the
// persisted shape is the seven-key map either way (C2).
const DAYS = [
  ["monday", "Mon"], ["tuesday", "Tue"], ["wednesday", "Wed"], ["thursday", "Thu"],
  ["friday", "Fri"], ["saturday", "Sat"], ["sunday", "Sun"],
];
const BREAK_OPTIONS = [0, 15, 30, 45, 60];

// C15 — the pinned copy. (a)/(b) share their second sentence.
const RECOMPUTE = "When it is resumed, their absent, rest-day and missing-day figures will be recomputed from the new schedule.";
const SINGLE_DRAFT_WARNING = `This employee is in a draft payroll run. ${RECOMPUTE}`;
const bulkDraftWarning = (n) =>
  `${n} of the selected employees ${n === 1 ? "is" : "are"} in a draft payroll run. ${RECOMPUTE}`;
const assignLabel = (n) => `Assign to ${n} ${n === 1 ? "employee" : "employees"}`;
const EMPTY_FILTERED = "No active employees match.";
const EMPTY_NONE = "No active employees.";
const NO_SCHEDULE = "No schedule";
const LOAD_FAILED = "Couldn't load work schedules — please retry.";
// C15 (e) {reason}: Firestore's message without its own trailing full stop —
// the server's text ("Missing or insufficient permissions.") already ends in one.
const reasonOf = (err) => (err?.message || "unknown error").replace(/\.$/, "");

const inputStyle = {
  padding: "0.5rem 0.75rem",
  border: "1px solid var(--ui-border)",
  borderRadius: "6px",
  fontSize: "0.8125rem",
  fontFamily: "inherit",
};

// hoursPerDay for the C16 summary bar — attendancePolicy is the home, payrollRates
// the pre-backfill fallback, 8 the default (StepEmployment.js:64-73).
const loadHoursPerDay = async (companyId) => {
  const policy = await getDoc(doc(db, "companies", companyId, "settings", "attendancePolicy"));
  const fromPolicy = policy.exists() ? policy.data().hoursPerDay : undefined;
  if (typeof fromPolicy === "number") return fromPolicy;
  const rates = await getDoc(doc(db, "companies", companyId, "settings", "payrollRates"));
  return rates.exists() ? (rates.data().hoursPerDay ?? 8) : 8;
};

// C11 — project ONLY what the roster and modals need, never the raw doc.
// `id` is the doc id (the write target); `employeeId` is the 4-digit number
// (the id the payroll index stores — C7).
const project = (d) => {
  const data = d.data();
  return {
    id: d.id,
    employeeId: data.employeeId,
    fullName:
      data.personalInfo?.fullName ||
      `${data.personalInfo?.firstName || ""} ${data.personalInfo?.lastName || ""}`.trim(),
    department: data.employment?.department || "",
    workSchedule: data.employment?.workSchedule,
  };
};

const initials = (name) =>
  (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const defaultForm = () => ({ workingDays: { ...DEFAULT_WORKING_DAYS }, ...SCHEDULE_DEFAULTS });

// Sort icon + sortable header — the AdjustmentsPage / EmployeeList pattern,
// local to this page. The pointer + hover wash live on the sortable columns
// only (C4): Working days / Hours / Break / Actions are plain `th`s.
const SortIcon = ({ field, sortField, sortDirection }) => {
  const isActive = sortField === field;
  const base = { marginLeft: "4px", verticalAlign: "middle" };
  if (isActive && sortDirection === "asc") return <Icon name="arrow-up" size={12} style={base} />;
  if (isActive && sortDirection === "desc") return <Icon name="arrow-down" size={12} style={base} />;
  return <Icon name="chevrons-up-down" size={12} style={{ ...base, opacity: 0.5 }} />;
};

const SortableTh = ({ field, label, style, sortField, sortDirection, onSort }) => {
  const [hover, setHover] = useState(false);
  return (
    <th
      onClick={() => onSort(field)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, cursor: "pointer", background: hover ? "rgba(255,255,255,0.1)" : undefined }}
    >
      {label}
      <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
    </th>
  );
};

const WorkSchedulePage = () => {
  const { user, company, hasPermission } = useAuth();
  const canEdit = hasPermission("sched:edit");
  const toast = useToast();
  const userDocId = user?.userData?.userDocId || user?.uid;

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]); // C11-projected, active today (C5)
  const [runs, setRuns] = useState([]); // payroll index entries, [] when the read failed (C7)
  const [hoursPerDay, setHoursPerDay] = useState(null); // null when the settings read failed (C16)

  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [sortField, setSortField] = useState("name"); // C4: name ascending by default
  const [sortDirection, setSortDirection] = useState("asc");

  const [editTarget, setEditTarget] = useState(null); // projected employee
  const [bulkOpen, setBulkOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Data load ─────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const today = todayManilaYMD();
      const [empSnap, hpd, indexRuns] = await Promise.all([
        getDocs(query(collection(db, "companies", company.id, "employees"), where("role", "==", "employee"))),
        loadHoursPerDay(company.id).catch((err) => {
          console.error("[WorkSchedulePage] hoursPerDay read failed — no OT/UT figures:", err);
          return null;
        }),
        loadActiveLocks(company.id).catch((err) => {
          console.error("[WorkSchedulePage] payroll index read failed — no draft-run warning:", err);
          return [];
        }),
      ]);
      // C5: the active filter runs on the RAW doc (the helper reads
      // employment.startDate/endDate) BEFORE projection; a soft-deleted doc is
      // never a roster row.
      setEmployees(
        empSnap.docs
          .filter((d) => !d.data().metadata?.isDeleted && isEmployeeActiveOnDate(d.data(), today))
          .map(project)
          .filter((e) => e.employeeId),
      );
      setHoursPerDay(hpd);
      setRuns(indexRuns);
    } catch (err) {
      console.error("[WorkSchedulePage] load failed:", err);
      toast.error(LOAD_FAILED);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [company?.id, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Derived view ──────────────────────────────────────────────────────────
  const departments = useMemo(
    () => [...new Set(employees.map((e) => e.department).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [employees],
  );

  const visibleRows = useMemo(() => {
    const filtered = employees.filter((e) => {
      if (departmentFilter && e.department !== departmentFilter) return false;
      return matchesEmployeeSearch(e, searchTerm);
    });
    // localeCompare so Ñ sorts among the N's (EmployeeList.js:136-140).
    const key = sortField === "department" ? (e) => e.department : (e) => e.fullName;
    return filtered.sort((a, b) => {
      const c = key(a).localeCompare(key(b));
      return sortDirection === "asc" ? c : -c;
    });
  }, [employees, searchTerm, departmentFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Footer: "{shown} of {active}" while a filter is on (C4; the total is the
  // active set — C5 is scope, not a filter).
  const filterActive = Boolean(searchTerm) || Boolean(departmentFilter);
  const countDisplay = formatFilteredCount(visibleRows.length, employees.length, filterActive);

  // ── Writes (C3 / C15 (e)) ─────────────────────────────────────────────────
  const handleSaveOne = async (schedule) => {
    setSaving(true);
    try {
      await updateSchedule(company.id, editTarget.id, schedule, userDocId);
      toast.success("Schedule updated.");
    } catch (err) {
      toast.error(`Couldn't update the schedule — ${reasonOf(err)}.`);
    } finally {
      setSaving(false);
      setEditTarget(null);
      fetchData();
    }
  };

  const handleSaveBulk = async ({ docIds, schedule }) => {
    setSaving(true);
    try {
      const { committed, failed, error } = await assignScheduleBulk(company.id, docIds, schedule, userDocId);
      if (failed > 0) {
        const total = committed + failed;
        toast.error(
          `Assigned ${committed} of ${total} ${total === 1 ? "employee" : "employees"} — ${reasonOf(error)}. Safe to re-run for the rest.`,
        );
      } else {
        toast.success(`Schedule assigned to ${committed} ${committed === 1 ? "employee" : "employees"}.`);
      }
    } catch (err) {
      toast.error(`Couldn't update the schedule — ${reasonOf(err)}.`);
    } finally {
      // C3: on ANY outcome the modal closes and the page refetches (never optimistic).
      setSaving(false);
      setBulkOpen(false);
      fetchData();
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const th = {
    padding: "14px 16px",
    textAlign: "left",
    fontWeight: "600",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "white",
    userSelect: "none",
    whiteSpace: "nowrap",
  };
  const td = { padding: "12px 16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" };

  const dayChips = (workingDays) => (
    <div style={{ display: "flex", gap: "0.25rem" }}>
      {DAYS.map(([id, label]) => {
        const on = workingDays[id];
        return (
          <span
            key={id}
            style={{
              padding: "0.125rem 0.375rem",
              borderRadius: "4px",
              fontSize: "0.6875rem",
              fontWeight: 600,
              background: on ? "var(--ui-primary-tint)" : "var(--ui-surface-recessed)",
              color: on ? "var(--ui-primary-hover)" : "var(--ui-text-faint)",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
        );
      })}
    </div>
  );

  const renderRow = (emp) => {
    const s = readSchedule(emp.workSchedule);
    return (
      <tr key={emp.id} style={{ borderBottom: "1px solid var(--ui-surface-recessed)" }}>
        <td style={td}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "var(--ui-primary-tint)",
                color: "var(--ui-primary)",
                fontSize: "13px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {initials(emp.fullName)}
            </div>
            <div>
              <div style={{ whiteSpace: "nowrap" }}>{emp.fullName}</div>
              <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontVariantNumeric: "tabular-nums" }}>
                EMP-{emp.employeeId}
              </div>
            </div>
          </div>
        </td>
        <td style={td}>{emp.department || "—"}</td>
        {s ? (
          <>
            <td style={td}>{dayChips(s.workingDays)}</td>
            <td style={{ ...td, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
              {s.startTime}–{s.endTime}
            </td>
            <td style={{ ...td, whiteSpace: "nowrap" }}>{s.breakDuration === 0 ? "No break" : `${s.breakDuration} min`}</td>
          </>
        ) : (
          <td style={{ ...td, color: "var(--ui-text-muted)", fontWeight: 400 }} colSpan={3}>
            {NO_SCHEDULE}
          </td>
        )}
        <td style={{ ...td, whiteSpace: "nowrap" }}>
          {canEdit && (
            <button
              onClick={() => setEditTarget(emp)}
              aria-label={`Edit schedule for ${emp.fullName}`}
              style={{
                padding: "0.375rem 0.625rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                background: "white",
                cursor: "pointer",
                fontSize: "0.8125rem",
              }}
            >
              <Icon name="pencil" size={14} />
            </button>
          )}
        </td>
      </tr>
    );
  };

  const countCard = (
    <div
      style={{
        position: "relative",
        background: "white",
        borderRadius: "8px",
        border: "1px solid var(--ui-border)",
        padding: "0.875rem 1rem",
        overflow: "hidden",
        marginBottom: "1.5rem",
      }}
    >
      <div style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: "4px", background: "var(--ui-primary)", borderRadius: "0 2px 2px 0" }} />
      <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>Active employees</div>
      <div style={{ fontSize: "18px", fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "var(--ui-text)" }}>{countDisplay}</div>
    </div>
  );

  return (
    <div style={{ fontFamily: "var(--font-family-primary)" }}>
      <div style={{ width: "100%" }}>
        <h1
          style={{
            fontSize: isMobile ? "1.125rem" : "1.25rem",
            fontWeight: "700",
            color: "var(--ui-primary)",
            margin: isMobile ? "0 0 1rem 0" : "0 0 0.75rem 0",
          }}
        >
          Work Schedule
        </h1>

        {isMobile && !loading && countCard}

        {/* Controls bar (C4: search + department only) */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: "0.75rem",
            padding: isMobile ? "1rem" : "0.625rem 1rem",
            background: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            marginBottom: "0.75rem",
          }}
        >
          <input
            type="text"
            placeholder="Search employee…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: "140px" }}
          />
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            aria-label="Department"
            style={{ ...inputStyle, minWidth: "140px" }}
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          {canEdit && (
            <>
              {!isMobile && <div style={{ width: "1px", height: "24px", background: "var(--ui-border)", flexShrink: 0 }} />}
              <button
                onClick={() => setBulkOpen(true)}
                style={{
                  padding: "0.5rem 1rem",
                  background: "var(--ui-primary)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "0.8125rem",
                  cursor: "pointer",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.375rem",
                }}
              >
                <Icon name="users" size={14} /> Bulk Assign
              </button>
            </>
          )}
        </div>

        {/* Table */}
        <div style={{ background: "white", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflowX: "auto", overflowY: "hidden" }}>
          {loading ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--ui-text-muted)" }}>Loading work schedules…</div>
          ) : visibleRows.length === 0 ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--ui-text-muted)" }}>
              {filterActive ? EMPTY_FILTERED : EMPTY_NONE}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--ui-gradient-primary)" }}>
                  <SortableTh field="name" label="Employee" style={th} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableTh field="department" label="Department" style={th} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <th style={th}>Working Days</th>
                  <th style={th}>Hours</th>
                  <th style={th}>Break</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>{visibleRows.map(renderRow)}</tbody>
            </table>
          )}

          {/* Footer (desktop) */}
          {!isMobile && !loading && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 20px",
                background: "linear-gradient(135deg, var(--ui-bg) 0%, var(--ui-surface-recessed) 100%)",
                borderTop: "1px solid var(--ui-border)",
              }}
            >
              <div>
                <div
                  data-testid="ws-footer-count"
                  style={{ fontSize: "20px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-primary)" }}
                >
                  {countDisplay}
                </div>
                <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
                  Active employees
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {editTarget && (
        <EditScheduleModal
          employee={editTarget}
          runs={runs}
          hoursPerDay={hoursPerDay}
          saving={saving}
          onClose={() => setEditTarget(null)}
          onSave={handleSaveOne}
        />
      )}

      {bulkOpen && (
        <BulkAssignModal
          employees={employees}
          runs={runs}
          hoursPerDay={hoursPerDay}
          saving={saving}
          onClose={() => setBulkOpen(false)}
          onSave={handleSaveBulk}
        />
      )}
    </div>
  );
};

// ── Shared schedule form (C2 shape; C16 summary bar) ───────────────────────────────
const ScheduleForm = ({ form, onChange, hoursPerDay }) => {
  const label = { fontSize: "0.75rem", fontWeight: 600, color: "var(--ui-text-secondary)", marginBottom: "0.25rem", display: "block" };
  const schedule = buildSchedule(form);
  const otUt = scheduleOtUt(schedule, hoursPerDay);
  const toggleDay = (id) =>
    onChange({ ...form, workingDays: { ...form.workingDays, [id]: !form.workingDays[id] } });

  return (
    <>
      <span style={label}>Working days</span>
      <div style={{ display: "flex", gap: "0.375rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        {DAYS.map(([id, text]) => {
          const on = !!form.workingDays[id];
          return (
            <button
              key={id}
              type="button"
              aria-pressed={on}
              onClick={() => toggleDay(id)}
              style={{
                padding: "0.4rem 0.75rem",
                borderRadius: "6px",
                border: on ? "none" : "1px solid var(--ui-border)",
                background: on ? "var(--ui-primary)" : "white",
                color: on ? "white" : "var(--ui-text-muted)",
                fontSize: "0.8125rem",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              {text}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <label style={{ ...label, flex: 1, minWidth: "110px" }}>
          Start time
          <input
            type="time"
            value={form.startTime}
            onChange={(e) => onChange({ ...form, startTime: e.target.value })}
            style={{ ...inputStyle, display: "block", width: "100%", marginTop: "0.25rem" }}
          />
        </label>
        <label style={{ ...label, flex: 1, minWidth: "110px" }}>
          End time
          <input
            type="time"
            value={form.endTime}
            onChange={(e) => onChange({ ...form, endTime: e.target.value })}
            style={{ ...inputStyle, display: "block", width: "100%", marginTop: "0.25rem" }}
          />
        </label>
        <label style={{ ...label, flex: 1, minWidth: "110px" }}>
          Break
          <select
            value={form.breakDuration}
            onChange={(e) => onChange({ ...form, breakDuration: parseInt(e.target.value, 10) })}
            style={{ ...inputStyle, display: "block", width: "100%", marginTop: "0.25rem" }}
          >
            {BREAK_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m === 0 ? "No break" : `${m} min`}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* C16 — Step 3's summary bar (StepEmployment.js:1072-1114; the wizard is frozen, C8) minus its Rest
          and Based-on segments. Work Days always; Regular / OT / UT only when hoursPerDay loaded and a
          working day has both times. */}
      <div
        className="ws-summary-bar"
        data-testid="ws-summary-bar"
        style={{
          background: otUt && (otUt.ot > 0 || otUt.ut > 0) ? "var(--ui-warning-tint)" : "var(--ui-bg)",
          marginBottom: "0.75rem",
        }}
      >
        <span style={{ color: "var(--ui-text-muted)" }}>Work Days: </span>
        <span style={{ color: "var(--ui-primary)", fontWeight: "600" }}>
          {Object.values(schedule).filter((d) => d?.isWorkingDay).length} days/week
        </span>
        {otUt && (
          <>
            <span style={{ color: "var(--ui-border)", margin: "0 0.25rem" }}>|</span>
            <span style={{ color: "var(--ui-text-muted)" }}>Regular: </span>
            <span style={{ fontWeight: "600", color: "var(--ui-text)" }}>{otUt.regularHours}h</span>
            <span style={{ color: "var(--ui-border)", margin: "0 0.25rem" }}>|</span>
            <span style={{ color: "var(--ui-text-muted)" }}>OT: </span>
            <span style={{ fontWeight: "600", color: otUt.ot > 0 ? "var(--ui-warning-hover)" : "var(--ui-text)" }}>
              {otUt.ot.toFixed(2)}h
            </span>
            <span style={{ color: "var(--ui-border)", margin: "0 0.25rem" }}>|</span>
            <span style={{ color: "var(--ui-text-muted)" }}>UT: </span>
            <span style={{ fontWeight: "600", color: otUt.ut > 0 ? "var(--ui-danger)" : "var(--ui-text)" }}>
              {otUt.ut.toFixed(2)}h
            </span>
          </>
        )}
      </div>
    </>
  );
};

// The body scrolls, not the dialog, so the shared <Modal>'s × stays pinned.
const modalBox = { background: "white", borderRadius: "12px", maxWidth: "520px", width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" };
const modalBody = { padding: "1.5rem", overflowY: "auto" };
const warnBand = { background: "var(--ui-warning-tint)", color: "var(--ui-warning-text)", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.8125rem", marginBottom: "0.75rem" };
const errorBand = { background: "var(--ui-danger-tint)", color: "var(--ui-danger-text)", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.8125rem", marginBottom: "0.75rem" };
const primaryBtn = (disabled) => ({
  padding: "0.5rem 1rem",
  background: "var(--ui-primary)",
  color: "white",
  border: "none",
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: "0.8125rem",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
});

// Title only — the × is the shared <Modal>'s (PRD_ModalBehavior C3).
const ModalHeader = ({ id, title }) => (
  <div style={{ marginBottom: "1rem", paddingRight: "2.5rem" }}>
    <h3 id={id} style={{ margin: 0, fontSize: "1rem", color: "var(--ui-text)" }}>{title}</h3>
  </div>
);

// ── Edit one employee (C2, C7 warning, C15 (a)) ─────────────────────────────
const EditScheduleModal = ({ employee, runs, hoursPerDay, saving, onClose, onSave }) => {
  // Preload from the stored schedule (first working day, C2); a "No schedule"
  // row opens on the defaults (C4).
  const [form, setForm] = useState(() => readSchedule(employee.workSchedule) || defaultForm());
  const [error, setError] = useState(null);
  const titleId = useId();
  const inDraft = draftRunCount(runs, [employee.employeeId]) > 0;

  const submit = () => {
    const problem = validateSchedule(form);
    setError(problem);
    if (problem) return;
    onSave(buildSchedule(form));
  };

  return (
    <Modal isOpen onClose={onClose} busy={saving} labelledBy={titleId} style={modalBox}>
      <div style={modalBody}>
        <ModalHeader id={titleId} title={`Edit schedule — ${employee.fullName}`} />
        <ScheduleForm form={form} onChange={setForm} hoursPerDay={hoursPerDay} />
        {inDraft && <div style={warnBand}>{SINGLE_DRAFT_WARNING}</div>}
        {error && <div style={errorBand}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
          <button onClick={onClose} disabled={saving} style={{ ...inputStyle, cursor: "pointer", background: "white" }}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving} style={primaryBtn(saving)}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── Bulk assign (C3; picker lifted from BulkAdjustmentModal; C7 aggregate
// warning; C15 (b)/(c)). Selection is keyed by employeeId — the id the payroll
// index stores — and doc ids are resolved at submit.
const BulkAssignModal = ({ employees, runs, hoursPerDay, saving, onClose, onSave }) => {
  const [form, setForm] = useState(defaultForm);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const titleId = useId();

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byDept = new Map();
    employees
      .filter((e) => !q || `${e.fullName} ${e.employeeId}`.toLowerCase().includes(q))
      .forEach((e) => {
        const key = e.department || "No department";
        if (!byDept.has(key)) byDept.set(key, []);
        byDept.get(key).push(e);
      });
    return [...byDept.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [employees, search]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleGroup = (members, allOn) =>
    setSelected((prev) => {
      const next = new Set(prev);
      members.forEach((e) => (allOn ? next.delete(e.employeeId) : next.add(e.employeeId)));
      return next;
    });

  const n = selected.size;
  const inDraft = draftRunCount(runs, [...selected]);
  const blocked = n === 0;

  const submit = () => {
    const problem = validateSchedule(form);
    setError(problem);
    if (problem) return;
    const docIds = employees.filter((e) => selected.has(e.employeeId)).map((e) => e.id);
    onSave({ docIds, schedule: buildSchedule(form) });
  };

  const label = { fontSize: "0.75rem", fontWeight: 600, color: "var(--ui-text-secondary)", marginBottom: "0.25rem", display: "block" };
  const rowLabel = { display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" };

  return (
    <Modal isOpen onClose={onClose} busy={saving} labelledBy={titleId} style={modalBox}>
      <div style={modalBody}>
        <ModalHeader id={titleId} title="Bulk Assign Schedule" />
        <ScheduleForm form={form} onChange={setForm} hoursPerDay={hoursPerDay} />

        <span style={label}>Employees ({n} selected)</span>
        <input
          type="text"
          placeholder="Search employees…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, display: "block", width: "100%", marginBottom: "0.75rem" }}
        />
        <div style={{ border: "1px solid var(--ui-border)", borderRadius: "6px", maxHeight: "220px", overflowY: "auto", marginBottom: "0.75rem" }}>
          {groups.length === 0 && (
            <div style={{ padding: "0.75rem", color: "var(--ui-text-muted)", fontSize: "0.8125rem" }}>No employees match.</div>
          )}
          {groups.map(([dept, members]) => {
            const allOn = members.every((e) => selected.has(e.employeeId));
            return (
              <div key={dept}>
                <label style={{ ...rowLabel, padding: "0.5rem 0.75rem", background: "var(--ui-surface-recessed)", fontSize: "0.75rem", fontWeight: 600 }}>
                  <input type="checkbox" checked={allOn} onChange={() => toggleGroup(members, allOn)} />
                  {dept} ({members.length})
                </label>
                {members.map((e) => (
                  <label key={e.employeeId} style={{ ...rowLabel, padding: "0.375rem 0.75rem 0.375rem 1.75rem", fontSize: "0.8125rem" }}>
                    <input type="checkbox" checked={selected.has(e.employeeId)} onChange={() => toggle(e.employeeId)} />
                    {e.fullName}
                    <span style={{ color: "var(--ui-text-muted)", fontVariantNumeric: "tabular-nums" }}>EMP-{e.employeeId}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>

        {inDraft > 0 && <div style={warnBand}>{bulkDraftWarning(inDraft)}</div>}
        {error && <div style={errorBand}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
          <button onClick={onClose} disabled={saving} style={{ ...inputStyle, cursor: "pointer", background: "white" }}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving || blocked} style={primaryBtn(saving || blocked)}>
            {saving ? "Saving…" : assignLabel(n)}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default WorkSchedulePage;
